import React, { useState, useEffect, useCallback } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col, Spinner, Accordion, Card } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { FormikSelect, FormikInput, FormikDatePicker } from '../../components';
import { periods } from './FormOptions';
import { indexEmployeesRequest } from '../../requests/employees';
import EmployeeDncInputs from './EmployeeDncInputs';

const resultFetchWorkers = response => {
  const result = response.data.data;
  return result.map(element => ({
    ...element,
    label: element.full_name,
    value: element.id
  }));
};

const DncForm = ({
  errors,
  touched,
  values,
  setFieldValue,
  setFieldTouched,
  dnc,
  isSubmitting,
  onNameChange,
  employeeParams
}) => {
  const [onRequest, setOnRequest] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [activeGeneral, setActiveGeneral] = useState(true);
  const [activeTrainings, setActiveTrainings] = useState(true);
  const dispatch = useDispatch();
  const { period, employeeId, year } = dnc;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, i) => {
    const y = currentYear - 1 + i;
    return { label: y.toString(), value: y };
  });

  const fetchWorkers = useCallback(() => {
    setOnRequest(true);
    indexEmployeesRequest({
      dispatch,
      params: {
        active: true,
        is_dt: false,
        paginate: false,
        sort_column: 'name',
        ...employeeParams
      },
      successCallback: res => setWorkers(resultFetchWorkers(res)),
      callback: () => setOnRequest(false)
    });
  }, [dispatch, employeeParams]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    const currentEmployeeId = values.dnc.employeeId;
    if (values.dnc.employeeDncsAttributes) {
      const updated = values.dnc.employeeDncsAttributes.map(item => ({
        ...item,
        employeeId: currentEmployeeId
      }));
      if (JSON.stringify(updated) !== JSON.stringify(values.dnc.employeeDncsAttributes)) {
        setFieldValue('dnc[employeeDncsAttributes]', updated);
      }
    }
  }, [values.dnc.employeeId, setFieldValue, values.dnc.employeeDncsAttributes]);

  useEffect(() => {
    if (onNameChange) {
      onNameChange(values.dnc.name);
    }
  }, [values.dnc.name, onNameChange]);

  const syncDncData = (selectedYear, selectedPeriod) => {
    const periodLabel = periods.find(p => p.value === selectedPeriod)?.label || '';
    setFieldValue('dnc[name]', `DNC ${selectedYear} - ${periodLabel}`);
    setFieldValue('dnc[limitDate]', new Date(selectedYear, 11, 31));
  };

  const onYearChange = data => {
    const selectedYear = data ? data.value : '';
    setFieldValue('dnc[year]', selectedYear);
    syncDncData(selectedYear, values.dnc.period);
  };

  const onPeriodChange = data => {
    const selectedPeriod = data ? data.value : '';
    setFieldValue('dnc[period]', selectedPeriod);
    syncDncData(values.dnc.year, selectedPeriod);
  };

  return (
    <>
      {onRequest && (
        <div
          className="containerSpinnerLoad position-absolute h-100 w-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 10, top: 0, left: 0 }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      <Form className={`${onRequest && 'bg-opacity'} mx-1 mx-md-3`}>
        <div className="yellow-wrapper">
          <Accordion defaultActiveKey="general" className="mb-3">
            <Col className={`${activeGeneral ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
              <Accordion.Toggle
                as={Col}
                eventKey="general"
                className="card-header-title pl-0"
                onClick={() => setActiveGeneral(!activeGeneral)}
                style={{ cursor: 'pointer' }}
              >
                <span>Datos Generales</span>
              </Accordion.Toggle>
            </Col>

            <Accordion.Collapse eventKey="general">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Field name="dnc[year]">
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            label="Año"
                            placeholder="Seleccionar año"
                            options={years}
                            defaultValue={year}
                            onChange={onYearChange}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={4}>
                      <Field name="dnc[period]">
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            label="Periodo"
                            placeholder="Seleccionar periodo"
                            options={periods}
                            defaultValue={period}
                            onChange={onPeriodChange}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                    <Col md={4}>
                      <Field name="dnc[employeeId]">
                        {({ field }) => (
                          <FormikSelect
                            {...field}
                            abbr
                            label="Trabajador"
                            options={workers}
                            defaultValue={employeeId}
                            placeholder="Seleccionar trabajador"
                            onChange={data => {
                              setFieldValue(field.name, data ? data.value : '');
                              if (data && data.job_management) {
                                setFieldValue('dnc[jobManagementId]', data.job_management.id);
                              }
                            }}
                            error={getIn(errors, field.name)}
                            touched={getIn(touched, field.name)}
                          />
                        )}
                      </Field>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Accordion.Collapse>
          </Accordion>

          <Accordion defaultActiveKey="trainings" className="mb-3">
            <Col className={`${activeTrainings ? 'top-header-green' : 'top-header-light-gray'} br-5`}>
              <Accordion.Toggle
                as={Col}
                eventKey="trainings"
                className="card-header-title pl-0"
                onClick={() => setActiveTrainings(!activeTrainings)}
                style={{ cursor: 'pointer' }}
              >
                <span>Capacitaciones Requeridas</span>
              </Accordion.Toggle>
            </Col>

            <Accordion.Collapse eventKey="trainings">
              <Card>
                <Card.Body>
                  <EmployeeDncInputs
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    employeeId={values.dnc.employeeId}
                  />
                </Card.Body>
              </Card>
            </Accordion.Collapse>
          </Accordion>
        </div>

        <Row className="d-none">
          <Col md={6}>
            <Field name="dnc[name]">{({ field }) => <FormikInput {...field} label="Nombre (Auto)" />}</Field>
            <Field name="dnc[limitDate]">{({ field }) => <FormikDatePicker {...field} />}</Field>
          </Col>
        </Row>

        <Row className="d-flex justify-content-end my-5">
          <Col md={3}>
            <Button type="submit" disabled={isSubmitting} block>
              Guardar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

const setInitialValues = props => {
  const { dnc } = props;
  const currentYear = new Date().getFullYear();

  const initialAttributes = dnc.employeeDncsAttributes.map(item => ({ ...item }));
  if (initialAttributes.length === 0) {
    initialAttributes.push({
      active: true,
      cost: 0,
      depthLevel: '',
      urgencyLevel: '',
      status: 'pending',
      topicId: '',
      necessityAreaId: '',
      employeeId: dnc.employeeId || ''
    });
  }

  return {
    dnc: {
      ...dnc,
      year: dnc.year || currentYear,
      name: dnc.name || `DNC ${currentYear}`,
      limitDate: dnc.limitDate || new Date(currentYear, 11, 31),
      employeeDncsAttributes: initialAttributes
    }
  };
};

const validationSchema = Yup.object().shape({
  dnc: Yup.object().shape({
    period: Yup.string().required('Debes seleccionar un periodo'),
    employeeId: Yup.string().required('Debes seleccionar un trabajador'),
    year: Yup.string().required('Debes seleccionar un año'),
    employeeDncsAttributes: Yup.array().of(
      Yup.object().shape({
        topicId: Yup.string().required('Requerido'),
        depthLevel: Yup.string().required('Requerido'),
        urgencyLevel: Yup.string().required('Requerido')
      })
    )
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: props => props.action !== 'new'
})(DncForm);
